// hooks/useLeagues.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { logAndThrow } from '../utils/logging';
import { League } from '../types';

export const LEAGUES_QUERY_KEY = ['leagues'] as const;

async function fetchLeagues(): Promise<League[]> {
    const { data, error } = await supabase
        .from('leagues')
        .select('id, name, name_ar, code, level, parent_league_id, is_active, kind')
        .order('level', { ascending: true })
        .order('name', { ascending: true });

    if (error) return logAndThrow('fetch leagues', error);
    return data || [];
}

export const createLeaguesQueryOptions = () => ({
    queryKey: LEAGUES_QUERY_KEY,
    queryFn: fetchLeagues,
    staleTime: 30 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
});

export function useLeagues(options: { enabled?: boolean } = {}) {
    return useQuery<League[]>({
        ...createLeaguesQueryOptions(),
        enabled: options.enabled !== false,
    });
}

export function useCreateLeague() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (league: Partial<League>) => {
            const { data, error } = await supabase
                .from('leagues')
                .insert(league)
                .select()
                .single();

            if (error) return logAndThrow('create league', error, { league });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: LEAGUES_QUERY_KEY });
        },
    });
}